import { InlinePicture } from '@/components/composer2/InlinePicture';
/**
 * The composer: one post on a canvas, drawn the way the platform will show it. The format is
 * whatever is on the canvas. Assist (write, pick, fix) never changes a photo; Imagine makes
 * pictures and badges them. Everything here goes through the real post, version, asset,
 * generation and schedule endpoints.
 */
import type { ReelJobDto } from '@str/shared';
import type { GenerationDto, InspirationDto, MediaDto, PostDto, VersionDto } from '@str/api/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  joinCaption,
  newCardEdit,
  propertyChannels,
  reelQuote,
  splitCaption,
  CREDIT_PACK,
  CHANNEL_META,
  PLAN_META,
  PLANS,
  cardCountFor,
  cardsPromisedBy,
  creditSpend,
  imagineAllowed,
  generationTarget,
  isScheduleTime,
  type Canvas,
  type CardEdit,
  type Channel,
  type PostTemplate,
  type GenerationTarget,
  type GenerationOperation,
  type HostVideoCopy,
  type PostFormat,
  type PostLinkInput,
  type TikTokOptions,
} from '@str/shared';
import { useCallback, useEffect, useRef, useState, type DragEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';
import {
  ApiError,
  cancelRender,
  creditShortfall,
  errorMessage,
  uploadFile,
  type CreditShortfall,
} from '@/lib/api';
import { CreditsShort } from '@/components/CreditsShort';
import {
  WORDS_NOTE,
  calmerBand,
  cardsNeedingWords,
  lookFor,
  needsWords,
  wordsFor,
  wordsLayer,
} from '@/lib/auto-text';
import {
  cardSource,
  mediaSource,
  persistCards,
  renderCrop,
  sourceUrl,
  storeCardAsset,
  type ComposerCard,
  wordsBase,
} from '@/lib/composer-cards';
import { sampleBands } from '@/lib/picture-bands';
import { checkComposerFile } from '@/lib/composer-media';
import { startPoll, type PollHandle } from '@/lib/poll';
import {
  AUTO_FIX,
  LOOKS,
  formatFor,
  formatLabel,
  lookOf,
  parseHashtag,
  redoTarget,
  undoTarget,
} from '@/lib/composer-v2';
import { dayWithMonth } from '@/lib/calendar-move';
import { dayLabel, money, toIsoDate } from '@/lib/format';
import { useActiveProperty } from '@/lib/property-store';
import {
  fetchPost,
  fetchReferences,
  keys,
  postsApi,
  useChannels,
  useMedia,
  usePost,
  usePosts,
  useProperty,
  type Me,
  type PropertyDetail,
  billingApi,
} from '@/lib/queries';
import { createReelPost, reelDraft, reelSource, startReelJob, type ReelDraft } from '@/lib/reel';
import { mediaStyle } from '@/lib/scenes';
import { PostDetails } from '@/components/composer/PostDetails';
import { ReelSheet, type ReelTool } from '@/components/composer/ReelSheet';
import { ReelSoundStep } from '@/components/composer/ReelSoundStep';
import { PostPreview, type PreviewChannel } from '@/components/PostPreview';
import { PublishStatus } from '@/components/PublishStatus';
import { FilmedFrom } from '@/components/composer2/FilmedFrom';
import { PostPropertyBoundary } from '@/components/composer2/PostPropertyBoundary';
import { GenerationDetails } from '@/components/composer2/GenerationDetails';
import { hostWords } from '@/lib/draft-name';
import { FlowHeader } from '@/components/composer2/FlowHeader';
import { debugOn } from '@/lib/debug';
import { GenerationCandidate } from '@/components/composer2/GenerationCandidate';
import { MakingPanel } from '@/components/composer2/MakingPanel';
import { EditorControls, EditorStage, type EditorTool } from '@/components/composer2/PictureEditor';
import { draftEdit, startDraft, focusText, type PictureDraft } from '@/lib/picture-draft';
import { ArtworkSheet, LibrarySheet } from '@/components/composer2/sheets';
import { Wizard, type ReelStart, type WizardMake } from '@/components/composer2/Wizard';
import { useCreditsSheet } from '@/components/CreditsSheetHost';
import {
  loadNext,
  loadWizard,
  mediaModeFor,
  saveNext,
  saveWizard,
  wizardBrief,
} from '@/lib/wizard';
import { useTrackView } from '@/lib/analytics';
import { hostTimeZone } from '@/lib/schedule';
import { ScheduleSheet } from './ScheduleSheet';
import '@/components/composer2/composer2.css';

type Idea = PropertyDetail['ideas'][number];
type Tool = EditorTool | 'Imagine' | 'Reel';
type Sheet = { kind: 'share' } | { kind: 'library'; replace?: number } | { kind: 'artwork' } | null;
type Toast = { text: string; action?: { label: string; run: () => void } };
type Gen = {
  title: string;
  statuses: string[];
  note: string;
  renderOf: string | null;
  /** Only the caption is being rewritten (J28). */
  captionOnly?: boolean;
};
/** An Imagine result waiting for keep or discard: the version it came from and the one it made. */
type Proposal = { parentId: string; resultId: string; index: number; action: string };

const RENDER_POLL_FAILURE_LIMIT = 20;
const CHANGE_PHOTO = ['Clear the sky', 'Remove clutter', 'Change season'];
const NEW_PICTURE = ['Another room', 'With guests'];
/** Change season asks which one first; the prompt then names it. */
const SEASONS = ['Winter', 'Spring', 'Summer', 'Fall'];
const seasonPrompt = (season: string) => `The same view in ${season.toLowerCase()}`;

/** The most a written change to one picture costs: a new picture. The planner may charge less. */
const CHANGE_MAX = creditSpend({ format: 'Image', mediaMode: 'imagine' });
/** A caption rewrite: the words only. */
const REWRITE = creditSpend({ format: 'Image', mediaMode: 'photos', target: 'copy' });

export function Composer() {
  const { active } = useActiveProperty();
  // Create tapped again (sidebar) carries a fresh token: a new post, whatever this one is doing.
  const fresh = (useLocation().state as { fresh?: string } | null)?.fresh ?? '';
  return (
    <PostPropertyBoundary>
      <ComposerBody key={`${active.id}:${fresh}`} />
    </PostPropertyBoundary>
  );
}

function ComposerBody() {
  const { active, registerDraft } = useActiveProperty();
  const navigate = useNavigate();
  const [params, setSearch] = useSearchParams();
  // The router state (Create's fresh token keys this body) rides along when the URL is rewritten:
  // dropped, the key changes and the page remounts under a make that is still running (J23).
  const routerState: unknown = useLocation().state;
  const setParams = (next: Record<string, string>) =>
    setSearch(next, { replace: true, state: routerState });
  const [debug] = useState(() => debugOn(`?${params.toString()}`));
  const qc = useQueryClient();
  const postId = params.get('post');
  useTrackView('composer_opened', { post_id: postId, source: params.get('from') });
  // The day the host picked on the calendar travels in the URL (router state does not survive
  // the URL rewrite when the post is created); it is the day the share sheet opens on.
  const [targetDate] = useState(() => {
    const d = params.get('date');
    return d && /^\d{4}-\d{2}-\d{2}$/.test(d) && d >= toIsoDate(new Date()) ? d : null;
  });
  // The time of the calendar plan's slot rides along the same way; the share sheet opens on it.
  const [targetTime] = useState(() => {
    const t = params.get('time');
    return isScheduleTime(t) ? t : null;
  });
  const slotQuery: Record<string, string> = {
    ...(targetDate ? { date: targetDate } : {}),
    ...(targetTime ? { time: targetTime } : {}),
  };
  const postQ = usePost(postId);
  const post = postQ.data ?? null;
  const storyboardJobs = useQuery({
    queryKey: ['reel-jobs', postId],
    enabled: !!postId && post?.format === 'Reel',
    queryFn: async () => {
      const response = await fetch(`/api/posts/${postId}/reels`, { credentials: 'include' });
      if (!response.ok) throw new Error('Could not load storyboard');
      const data: { jobs: ReelJobDto[] } = await response.json();
      return data.jobs;
    },
  });
  // A reel with a storyboard lives on its own page now, whichever link opened it here.
  const reelFlow =
    ['storyboard', 'render'].includes(params.get('mode') ?? '') ||
    (post?.format === 'Reel' && !!storyboardJobs.data?.length);
  const property = useProperty(active.id);
  const library = useMedia(active.id);
  const connected = useChannels(active.id);
  const posts = usePosts(active.id);

  const [cardIdxRaw, setCardIdx] = useState(0);
  const [tool, setTool] = useState<Tool | null>(null);
  const [draft, setDraft] = useState<PictureDraft | null>(null);
  const [gen, setGen] = useState<Gen | null>(null);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  /** The Change season pill is open, waiting for a season. */
  const [askSeason, setAskSeason] = useState(false);
  const [sheet, setSheet] = useState<Sheet>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const toastTimer = useRef<number | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  // Files from this device on their way in: the one busy the steps call "Adding…".
  const [uploading, setUploading] = useState(false);
  const [busyText, setBusyText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [firstFail, setFirstFail] = useState<string | null>(null);
  /** A run the generator could not finish can be asked for again, while its message shows. */
  // `run` is the same ask again; null means a reopened draft's first run: make it from its brief.
  const [retry, setRetry] = useState<{ error: string; run: (() => void) | null } | null>(null);
  const lastRun = useRef<(() => void) | null>(null);
  // The wallet could not cover a run: shown with the ways out, never as a generic error.
  const [shortfall, setShortfall] = useState<CreditShortfall | null>(null);
  const [buying, setBuying] = useState(false);
  const [over, setOver] = useState(false);
  const [preview, setPreview] = useState<PreviewChannel>('IG');
  // The frame grows with the stage: a phone-sized card is lost on a wide screen.
  const stageRef = useRef<HTMLDivElement>(null);
  const [previewWidth, setPreviewWidth] = useState(360);
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width ?? 0;
      setPreviewWidth(Math.round(Math.min(500, Math.max(360, w * 0.44))));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [post, gen]);
  const [drag, setDrag] = useState<{ from: number; over: number | null } | null>(null);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [ask, setAsk] = useState('');
  const [reelOpen, setReelOpen] = useState<false | ReelTool>(false);
  const [preserved, setPreserved] = useState<{ versionId: string; draft: ReelDraft } | null>(null);
  /** What the host asked for on the three steps, shown while it is being made. */
  const [moment, setMoment] = useState<string | null>(null);
  /** By hand: the tapped photos are being put in as cards, a few seconds, nothing generated. */
  const [placing, setPlacing] = useState(false);
  /** The generator's words being put on the pictures as text the host can move or delete. */
  const [wording, setWording] = useState(false);
  const wordsTried = useRef<Set<string>>(new Set());
  // The property's preferred channels, less any its tier does not include.
  // Make another like this on Home keeps the last post's slot and channels for this one.
  const [next] = useState(() => loadNext(active.id));
  const [channels, setChannels] = useState<Channel[]>(() => {
    const allowed = propertyChannels(active.plan);
    const kept = (next?.channels ?? []).filter((c) => allowed.includes(c));
    if (kept.length) return kept;
    const preferred = active.channels.filter((c) => allowed.includes(c));
    return preferred.length ? preferred : ['IG'];
  });
  // The caption is edited as words, hashtags and link, and stored as one string.
  const [bodyEdit, setBodyEdit] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState('');
  // Files the host added from this device while still on the three steps.
  const [justAdded, setJustAdded] = useState<MediaDto[] | null>(null);
  // Someone else's post is followed only when a page just handed it over (Inspiration, a post's
  // results say `follow`); a reference left from an earlier visit is let go before the wizard reads it.
  const arrivedWith = useLocation().state as { follow?: boolean } | null;
  useState(() => {
    const w = loadWizard(active.id);
    if (w?.following && !arrivedWith?.follow)
      saveWizard(active.id, { ...w, following: null, template: null, shapeId: 'idea' });
  });
  // The calendar plan opens Create on an idea (`?idea=`): once the ideas have loaded, its words go
  // in the steps as the moment, once, and the link lets go of it. Nothing is spent until Make it,
  // where the price is shown, as on every other way into a post.
  const ideaParam = params.get('idea');
  const [ideaTaken, setIdeaTaken] = useState(false);
  const [wizardKey, setWizardKey] = useState(0);
  if (!ideaTaken && ideaParam && !postId && property.data) {
    setIdeaTaken(true);
    const idea = property.data.ideas.find((i) => i.id === ideaParam);
    if (idea) {
      saveWizard(active.id, {
        kind: idea.format === 'Reel' ? 'Reel' : 'Post',
        path: 'ai',
        line: idea.brief,
        picks: [],
        shapeId: 'idea',
        input: {},
      });
      // The steps read what is kept when they mount: mount them again on the idea.
      setWizardKey((k) => k + 1);
    }
  }
  useEffect(() => {
    if (ideaTaken && ideaParam) setParams(slotQuery);
  });
  const fileInput = useRef<HTMLInputElement>(null);
  const pollRef = useRef<PollHandle | null>(null);
  const recoveredRender = useRef<string | null>(null);
  // The step rail lives in the header once there are steps; the wizard renders into this slot.
  const [railSlot, setRailSlot] = useState<HTMLElement | null>(null);
  const creditsSheet = useCreditsSheet();
  // A reel post made from the first screen, kept so a retry after buying credits does not make two.
  const reelPost = useRef<string | null>(null);

  const photos = (library.data ?? []).filter(
    (m) => m.kind === 'photo' && m.source !== 'generated' && m.source !== 'render',
  );
  // The host's own videos: what they filmed or imported, never the clips we made for them.
  const videos = (library.data ?? []).filter(
    (m) => m.kind === 'video' && !!m.url && m.source !== 'generated' && m.source !== 'render',
  );
  const localPhotos = new Map(photos.filter((m) => !!m.url).map((m) => [m.id, m.url!] as const));
  const version: VersionDto | null =
    post?.versions.find(
      (v) =>
        v.id === post.currentVersionId &&
        v.acceptance !== 'unjudged' &&
        v.acceptance !== 'rejected',
    ) ?? null;
  // A first result is reviewable before it becomes the editable, accepted draft.
  const initialCandidate = !post?.currentVersionId
    ? (post?.versions
        .filter((v) => v.acceptance === 'unjudged' && v.generationPlanId && !v.baseVersionId)
        .sort((a, b) => b.n - a.n)[0] ?? null)
    : null;
  const previewVersion = version ?? initialCandidate;
  const previewCards = previewVersion?.cards ?? [];
  const cards: ComposerCard[] = version?.cards ?? [];
  const cardIdx = Math.min(cardIdxRaw, Math.max(0, cards.length - 1));
  const card = cards[cardIdx] ?? null;
  const hasPost = !!post;
  const isReel = post?.format === 'Reel' && !!version?.videoUrl;
  const vertical = post?.format === 'Reel' || post?.format === 'Story';
  const canvas: Canvas = vertical ? '9:16' : (cards[0]?.edit?.canvas ?? '4:5');
  const empty = !post || (!cards.length && !version?.videoUrl && !gen);
  // Until a post exists the screen is the three steps: what, photos, shape.
  const wizard = !post && !gen;
  const meNow = qc.getQueryData<Me>(keys.me);
  const trialing = meNow?.subscription?.status === 'trialing';
  const canImagine = imagineAllowed(meNow?.subscription?.status);
  const credits = meNow?.credits ? { ...meNow.credits, trial: trialing } : null;
  const startCharge = meNow?.subscription
    ? `$${meNow.subscription.dayChargeCents / 100} today for ${meNow.subscription.planName} ${meNow.subscription.interval === 'year' ? 'annual' : 'monthly'}`
    : undefined;
  // The owner has not put this property on their plan: said upfront, with the way there, and
  // Make it is held. The 402 the server answers with stays as the backstop.
  const offPlan =
    active.role === 'owner' && !active.plan && !!meNow?.subscription?.active
      ? `Add ${active.name} to your plan to post from it`
      : null;
  const cheapestMonthly = Math.min(...PLANS.map((k) => PLAN_META[k].priceCents));
  const working = busy || !!gen || wording;
  const parts = post ? splitCaption(post.caption) : { body: '', link: null, hashtags: [] };
  const body = bodyEdit ?? parts.body;
  const caption = post ? joinCaption({ body, link: parts.link, hashtags: parts.hashtags }) : '';
  const ig = (connected.data?.connections ?? []).find(
    (c) => c.channel === 'IG' && c.status !== 'disconnected',
  );
  const previewChannels = [
    ...new Set(
      (connected.data?.connections ?? [])
        .filter((c) => c.status === 'connected')
        .map((c) => c.channel),
    ),
  ];
  const shownPreview: PreviewChannel = previewChannels.includes(preview as Channel)
    ? preview
    : (previewChannels[0] ?? 'preview');
  const previewConnection = (connected.data?.connections ?? []).find(
    (c) => c.channel === shownPreview && c.status === 'connected',
  );
  const handle = `@${(ig?.handle ?? active.name.toLowerCase().replace(/[^a-z0-9]/g, '')).replace(/^@/, '')}`;
  const ideas = (property.data?.ideas ?? []).filter((i) => !skipped.includes(i.id)).slice(0, 3);
  const versions = post?.versions ?? [];
  const undoId = undoTarget(versions, post?.currentVersionId ?? null);
  const redoId = redoTarget(versions, post?.currentVersionId ?? null);
  const lastChange = version?.note ?? null;

  const show = (t: Toast, ms = 5000) => {
    window.clearTimeout(toastTimer.current);
    setToast(t);
    toastTimer.current = window.setTimeout(() => setToast(null), ms);
  };
  // What to run again once the wallet can cover it (set with the shortfall, run after a pack).
  const retryRef = useRef<(() => void) | null>(null);
  /** A failure from the API: a credit shortfall gets its own panel, anything else a line. */
  const showError = (e: unknown, retry?: () => void) => {
    const short = creditShortfall(e);
    if (short) {
      retryRef.current = retry ?? null;
      setShortfall(short);
      setError(null);
    } else setError(errorMessage(e));
  };
  const buyPack = async () => {
    setBuying(true);
    try {
      const fresh = await billingApi.buyPack(1);
      await qc.invalidateQueries({ queryKey: keys.me });
      setShortfall(null);
      show({ text: `${CREDIT_PACK.credits} credits added · ${fresh.balance} in your wallet` });
      // The run the host asked for goes ahead now that it is covered.
      const retry = retryRef.current;
      retryRef.current = null;
      retry?.();
    } catch (e) {
      showError(e);
    } finally {
      setBuying(false);
    }
  };
  const startPlan = async () => {
    setBuying(true);
    try {
      const res = await billingApi.startPlanNow();
      await qc.invalidateQueries({ queryKey: keys.me });
      setShortfall(null);
      show({ text: `Your plan has started · ${res.credits.balance} credits in your wallet` });
      const retry = retryRef.current;
      retryRef.current = null;
      retry?.();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBuying(false);
    }
  };
  const shortPanel = shortfall && (
    <CreditsShort
      shortfall={shortfall}
      busy={buying}
      startCharge={startCharge}
      onBuyPack={() => void buyPack()}
      onStartPlan={() => void startPlan()}
    />
  );
  const setPost = useCallback(
    (p: PostDto) => {
      qc.setQueryData(keys.post(p.id), p);
      void qc.invalidateQueries({ queryKey: keys.posts(active.id) });
    },
    [qc, active.id],
  );
  const closeTool = () => {
    setTool(null);
    setDraft(null);
    setAskSeason(false);
  };
  const stopPolling = useCallback(() => {
    pollRef.current?.cancel();
    pollRef.current = null;
  }, []);
  useEffect(() => () => stopPolling(), [stopPolling]);

  /* ------------------------------------------------------------- generation, real */

  const finishGeneration = useCallback(
    (g: GenerationDto, onDone?: (p: PostDto) => void, onFailed?: () => void) => {
      pollRef.current = null;
      const label = gen?.title ?? 'this post';
      setGen(null);
      // However the run ended, the charge is settled now (a failed run gives its credits back, a
      // retry as one picture returned the other cards'): the counter follows.
      void qc.invalidateQueries({ queryKey: keys.me });
      if (g.status === 'failed' || !g.post) {
        const why = g.statusText || 'Generation failed';
        const message = g.advice ? `${why.replace(/\.?$/, '.')} ${g.advice}` : why;
        setError(message);
        if (g.post) setPost(g.post);
        const run = lastRun.current;
        setRetry(run || (g.post && !g.post.currentVersionId) ? { error: message, run } : null);
        onFailed?.();
        return;
      }
      setPost(g.post);
      setBodyEdit(null);
      // The review panel comes into view and says what to do; a toast would sit on its buttons.
      if (g.versionId && g.versionId !== g.post.currentVersionId) return;
      void qc.invalidateQueries({ queryKey: ['media', active.id] });
      if (onDone) onDone(g.post);
      else {
        const v = g.post.versions.find((x) => x.id === g.post!.currentVersionId);
        const n = v?.cards.length ?? 0;
        show({
          text: `✓ Ready · ${label}${n ? ` · ${formatLabel(g.post.format, n).toLowerCase()}` : ''}, caption, ${splitCaption(g.post.caption).hashtags.length} hashtags`,
          action: { label: 'Share ▸', run: () => setSheet({ kind: 'share' }) },
        });
      }
    },
    [active.id, qc, setPost, gen?.title],
  );

  const followGeneration = useCallback(
    (
      job: Pick<GenerationDto, 'id' | 'statusText'>,
      title: string,
      renderOf: string | null = null,
      onDone?: (p: PostDto) => void,
      onFailed?: () => void,
      /** What is being watched: one poller per post, a new run replaces the old one. */
      key: string = job.id,
      /** What the host asked for on this run, shown while it is made after the first. */
      note = '',
      captionOnly = false,
    ) => {
      const render = !!renderOf;
      setError(null);
      stopPolling();
      setGen({ title, statuses: [job.statusText], note, renderOf, captionOnly });
      let lastStatus = job.statusText;
      pollRef.current = startPoll<GenerationDto>(`post:${key}`, {
        first: 400,
        request: () => postsApi.poll(job.id),
        isDone: (g) => g.status !== 'running',
        onResult: (g) => {
          if (g.status === 'running') {
            // A new stage can move credits (a retry as one picture returns the other cards'),
            // so the wallet is read again whenever the run says something new.
            if (g.statusText !== lastStatus) void qc.invalidateQueries({ queryKey: keys.me });
            lastStatus = g.statusText;
            setGen((cur) =>
              cur && cur.statuses[cur.statuses.length - 1] !== g.statusText
                ? { ...cur, statuses: [...cur.statuses, g.statusText] }
                : cur,
            );
          } else finishGeneration(g, onDone, onFailed);
        },
        onError: (e, failures) => {
          const terminal = e instanceof ApiError && [401, 403, 404].includes(e.status);
          if (render && !terminal && failures < RENDER_POLL_FAILURE_LIMIT) {
            setGen((cur) =>
              cur
                ? { ...cur, statuses: [...cur.statuses, 'Reconnecting to your saved Reel…'] }
                : cur,
            );
            return true;
          }
          pollRef.current = null;
          setGen(null);
          showError(e);
          onFailed?.();
          return false;
        },
      });
    },
    [finishGeneration, stopPolling, qc],
  );

  // A saved draft whose reel render is still going, or failed while the host was away.
  useEffect(() => {
    const p = postQ.data;
    if (!p) return;
    const recovery = p.renderRecovery;
    if (recovery?.status === 'running' && recoveredRender.current !== recovery.id) {
      recoveredRender.current = recovery.id;
      followGeneration(recovery, 'Saving your reel edit', p.id, undefined, undefined, p.id);
      return;
    }
    // A draft opened while it is still being made: pick the run up where it is.
    const making = p.generationRecovery;
    if (making?.status === 'running' && recoveredRender.current !== making.id) {
      recoveredRender.current = making.id;
      lastRun.current = null;
      followGeneration(making, p.title || 'Making it', null, undefined, undefined, p.id);
    }
  }, [postQ.data, followGeneration]);

  const runGeneration = async (
    p: PostDto,
    title: string,
    note: string,
    target?: GenerationTarget,
    onDone?: (p: PostDto) => void,
    /** A run that did not start or did not finish; a refusal passes its error along. */
    onFailed?: (e?: unknown) => void,
    operation?: GenerationOperation,
    targetCardIds?: string[],
    hostVideoCopy?: HostVideoCopy,
  ) => {
    setError(null);
    setRetry(null);
    lastRun.current = () =>
      void runGeneration(
        p,
        title,
        note,
        target,
        onDone,
        onFailed,
        operation,
        targetCardIds,
        hostVideoCopy,
      );
    setShortfall(null);
    setProposal(null);
    closeTool();
    try {
      // A draft without an accepted version always creates a reviewable first candidate.
      const first = !p.currentVersionId;
      const request = first ? note || p.brief : note;
      const started = first
        ? await postsApi.generate(p.id, request, undefined, {
            operation: 'create',
            ...(hostVideoCopy ? { hostVideoCopy } : {}),
          })
        : operation || targetCardIds
          ? await postsApi.generate(p.id, note, target, { operation, targetCardIds })
          : await postsApi.generate(p.id, note, target);
      // The run took its credits: the counter follows at once.
      void qc.invalidateQueries({ queryKey: keys.me });
      const captionOnly =
        !first &&
        (operation ? operation === 'copy_edit' : (target ?? generationTarget(note)) === 'copy');
      followGeneration(started, title, null, onDone, onFailed, p.id, note, captionOnly);
    } catch (e) {
      setGen(null);
      if (e instanceof ApiError && e.code === 'generation_needs_clarification') setAsk(note);
      // A first make from the steps answers a credit shortfall itself, with the credits sheet.
      if (onFailed && !p.currentVersionId && creditShortfall(e)) {
        onFailed(e);
        return;
      }
      showError(
        e,
        () =>
          void runGeneration(
            p,
            title,
            note,
            target,
            onDone,
            onFailed,
            operation,
            targetCardIds,
            hostVideoCopy,
          ),
      );
      onFailed?.(e);
    }
  };

  const tryAgain = () => {
    if (retry?.run) retry.run();
    else if (post) void runGeneration(post, post.title || 'Making it', '');
  };

  const stopGeneration = async () => {
    if (gen?.renderOf) {
      try {
        const g = await cancelRender(gen.renderOf);
        stopPolling();
        const fresh = g.post ?? (await fetchPost(gen.renderOf));
        setPost(fresh);
        setGen(null);
        show({ text: g.statusText || 'Reel edit cancelled' });
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 404)) showError(e);
      }
      return;
    }
    // An AI generation runs to its end on the server; stopping just stops waiting for it.
    stopPolling();
    setGen(null);
    show({ text: 'Stopped waiting. The result lands in Drafts when it is done.' });
  };

  /**
   * The Inspiration post this one was made after, if a slice holding it is cached here. The
   * post carries only the id; the handle and the link stay with Inspiration, so with nothing
   * cached the line is left out rather than guessed.
   */
  const madeAfter =
    post?.madeAfter ??
    (post?.inspirationPostId
      ? qc
          .getQueriesData<InspirationDto>({ queryKey: ['inspiration'] })
          .flatMap(([, d]) => d?.posts ?? [])
          .find((ip) => ip.id === post.inspirationPostId)
      : undefined);

  /* ------------------------------------------------------------- the post */

  const ensurePost = async (input: {
    format: PostFormat;
    brief: string;
    mediaMode?: PostDto['mediaMode'];
    pinnedIds?: string[];
    referenceIds?: string[];
    /** The form of the Inspiration post followed, and its id: sent only when there is one. */
    template?: PostTemplate | null;
    inspirationPostId?: string | null;
  }): Promise<PostDto> => {
    if (post) return post;
    const p = await postsApi.create(active.id, {
      format: input.format,
      goal: 'book',
      brief: input.brief,
      channels,
      mediaMode: input.mediaMode ?? 'photos',
      referenceIds: input.referenceIds ?? [],
      pinnedIds: input.pinnedIds ?? [],
      ...(input.template ? { template: input.template } : {}),
      ...(input.inspirationPostId ? { inspirationPostId: input.inspirationPostId } : {}),
    });
    setPost(p);
    setParams({ post: p.id, ...slotQuery });
    return p;
  };

  /** The format follows the cards; the server checks it when a version is saved. */
  const withFormat = async (p: PostDto, next: ComposerCard[]): Promise<PostDto> => {
    if (p.format === 'Story' || p.format === 'Reel') return p;
    const want = formatFor(next.map(() => ({ kind: 'photo' as const })));
    if (want === p.format) return p;
    const updated = await postsApi.update(p.id, { format: want });
    setPost(updated);
    return updated;
  };

  const persist = async (p: PostDto, next: ComposerCard[], note: string) => {
    const shaped = await withFormat(p, next);
    const saved = await persistCards(shaped.id, shaped.currentVersionId ?? '', next, note);
    setPost(saved);
    return saved;
  };

  /**
   * The words the AI wrote for each card go on the picture as a text layer, one host version on
   * top of the AI one (so undo walks back to the bare pictures), rendered like the Text tool does.
   * The picture is measured first so the words sit in its calmer band.
   */
  const putWordsOn = async (p: PostDto, v: VersionDto) => {
    const look = lookFor(p.brief);
    if (look === 'none') return;
    setWording(true);
    try {
      const next: ComposerCard[] = [];
      const wanted = new Set(cardsNeedingWords(p.brief, v, p.mediaMode));
      for (const [i, card] of v.cards.entries()) {
        const words = wanted.has(i) ? wordsFor(p.brief, v.cards, i) : '';
        if (!words) {
          next.push(card);
          continue;
        }
        const base = card.edit ?? newCardEdit(wordsBase(card, localPhotos), canvas);
        const samples = await sampleBands(sourceUrl(base.source), base.crop);
        const layer = wordsLayer(words, look, base.canvas, `words-${i}`, {
          ...(samples ? { band: calmerBand(samples) } : {}),
        });
        const edit: CardEdit = { ...base, layers: [...base.layers, layer] };
        const rendered = await renderCrop(edit);
        const asset = await storeCardAsset(p.id, rendered.blob);
        next.push({ ...card, imageUrl: asset.url, mediaId: asset.id, edit: rendered.edit });
      }
      await persist(p, next, WORDS_NOTE);
    } catch (e) {
      showError(e);
    } finally {
      setWording(false);
    }
  };
  // Runs once per AI version: after a generation lands, or when such a draft is opened later.
  const putWordsRef = useRef(putWordsOn);
  useEffect(() => {
    putWordsRef.current = putWordsOn;
  });
  useEffect(() => {
    if (!post || gen || busy || wording) return;
    const v = needsWords(post);
    if (!v || wordsTried.current.has(v.id)) return;
    wordsTried.current.add(v.id);
    void putWordsRef.current(post, v);
  }, [post, gen, busy, wording]);

  const flatCard = async (p: PostDto, media: MediaDto, shape: Canvas): Promise<ComposerCard> => {
    const edit = newCardEdit(mediaSource(media), shape);
    const rendered = await renderCrop(edit, true);
    const asset = await storeCardAsset(p.id, rendered.blob);
    return {
      headline: '',
      body: '',
      altText: media.label,
      imageUrl: asset.url,
      mediaId: asset.id,
      sourceMediaId: media.id,
      edit: rendered.edit,
    };
  };

  const addMedia = async (items: MediaDto[]) => {
    const video = items.find((m) => m.kind === 'video');
    setBusy(true);
    setError(null);
    try {
      if (video) {
        if (post) throw new Error('A video makes a reel on its own. Start a new post for it.');
        const p = await ensurePost({
          format: 'Reel',
          brief: `${active.name} reel`,
          pinnedIds: [video.id],
        });
        await runGeneration(p, 'Your reel', '');
        return;
      }
      const usable = items.filter((m) => m.kind === 'photo' && !!m.url);
      if (!usable.length) throw new Error('These photos have no file to use.');
      const p = await ensurePost({
        format: formatFor(usable.map(() => ({ kind: 'photo' }))),
        brief: `Photos of ${active.name}`,
        pinnedIds: usable.map((m) => m.id).slice(0, 10),
      });
      const fresh: ComposerCard[] = [];
      for (const m of usable.slice(0, 10 - cards.length)) fresh.push(await flatCard(p, m, canvas));
      const next = [...cards, ...fresh];
      await persist(
        p,
        next,
        cards.length ? (fresh.length === 1 ? 'Picture added' : 'Pictures added') : 'Your photos',
      );
      // A photo the host put in is theirs to keep: pin it so a later take does not drop it.
      const pins = [...new Set([...p.pinnedIds, ...usable.map((m) => m.id)])].slice(0, 10);
      if (pins.length !== p.pinnedIds.length)
        setPost(await postsApi.update(p.id, { pinnedIds: pins }));
      setCardIdx(next.length - 1);
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const upload = async (files: FileList | File[]) => {
    setBusy(true);
    setUploading(true);
    setError(null);
    try {
      const added: MediaDto[] = [];
      for (const file of Array.from(files)) {
        await checkComposerFile(file, file.type.startsWith('video/') ? 'Reel' : 'Image');
        const { media } = await uploadFile(active.id, file);
        added.push(media);
      }
      void qc.invalidateQueries({ queryKey: ['media', active.id] });
      setBusy(false);
      setUploading(false);
      // On the three steps the files are chosen, not yet made into a post.
      if (wizard) setJustAdded(added);
      else await addMedia(added);
    } catch (e) {
      showError(e);
      setBusy(false);
      setUploading(false);
    }
  };

  const replacePhoto = async (index: number, media: MediaDto) => {
    if (!post) return;
    setBusy(true);
    try {
      const fresh = await flatCard(post, media, canvas);
      const next = cards.map((c, i) =>
        i === index ? { ...fresh, headline: c.headline, body: c.body } : c,
      );
      await persist(post, next, 'Photo replaced');
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  const arrange = async (next: ComposerCard[], note: string) => {
    if (!post || working) return;
    setBusy(true);
    setError(null);
    try {
      await persist(post, next, note);
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------- the editor */

  const openTool = (t: Tool) => {
    if (!card && t !== 'Reel') return;
    if (tool === t) return closeTool();
    setProposal(null);
    if (t === 'Reel') {
      setReelOpen('Clips');
      return;
    }
    if (t === 'Imagine') {
      setDraft(null);
      setTool(t);
      return;
    }
    try {
      setDraft((cur) => {
        const next = cur ?? startDraft(card!, canvas, localPhotos);
        return t === 'Text' ? focusText(next) : next;
      });
      setTool(t);
      setError(null);
    } catch (e) {
      showError(e);
    }
  };

  const doneEditing = async (rethrow = false) => {
    if (!post || !draft || !tool || tool === 'Imagine' || tool === 'Reel') return;
    const edit = draftEdit(draft);
    setBusy(true);
    setError(null);
    try {
      const next = [...cards];
      const shapeChanged = edit.canvas !== canvas;
      for (let i = 0; i < cards.length; i++) {
        const c = cards[i]!;
        if (i !== cardIdx && !shapeChanged && !draft.applyToAll) continue;
        const base =
          i === cardIdx
            ? edit
            : {
                ...(c.edit ?? newCardEdit(cardSource(c, localPhotos), edit.canvas)),
                canvas: edit.canvas,
                ...(draft.applyToAll ? { adjust: edit.adjust } : {}),
              };
        const rendered = await renderCrop(base, i !== cardIdx && shapeChanged);
        const asset = await storeCardAsset(post.id, rendered.blob);
        next[i] = { ...c, mediaId: asset.id, imageUrl: asset.url, edit: rendered.edit };
      }
      const look = LOOKS.find((l) => l.key === lookOf(edit.adjust));
      const note =
        tool === 'Crop'
          ? shapeChanged
            ? `Shape ${edit.canvas}`
            : 'Cropped'
          : tool === 'Filters'
            ? `Filter ${look?.label ?? ''}`.trim()
            : tool === 'Adjust'
              ? 'Adjusted'
              : tool === 'Text'
                ? edit.layers.filter((l) => l.kind === 'text').length <
                  (card?.edit?.layers.filter((l) => l.kind === 'text').length ?? 0)
                  ? 'Text removed'
                  : 'Text edited'
                : 'Sticker added';
      await persist(post, next, draft.applyToAll ? `${note} · all` : note);
      closeTool();
    } catch (e) {
      showError(e);
      if (rethrow) throw e;
    } finally {
      setBusy(false);
    }
  };

  const fixAll = async () => {
    if (!post || !cards.length) return;
    setBusy(true);
    setError(null);
    try {
      const next: ComposerCard[] = [];
      for (const c of cards) {
        const base = {
          ...(c.edit ?? newCardEdit(cardSource(c, localPhotos), canvas)),
          adjust: { ...AUTO_FIX },
        };
        const rendered = await renderCrop(base);
        const asset = await storeCardAsset(post.id, rendered.blob);
        next.push({ ...c, mediaId: asset.id, imageUrl: asset.url, edit: rendered.edit });
      }
      await persist(post, next, 'Auto-fix light');
      show({ text: 'Done · Auto-fix light on every picture. Same photos.' });
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };

  /* ------------------------------------------------------------- Assist */

  /**
   * The described moment: the matched photos ground a completely new picture of the same place,
   * so the run goes on the reimagine road unless the caller says otherwise.
   */
  const makeFromBrief = async (
    input: {
      title: string;
      brief: string;
      format: PostFormat;
      pinnedIds?: string[];
      mediaMode?: PostDto['mediaMode'];
      template?: PostTemplate | null;
      inspirationPostId?: string | null;
    },
    after?: { onDone: () => void; onFailed: (p: PostDto, e?: unknown) => void },
  ) => {
    setBusy(true);
    setError(null);
    try {
      const refs =
        input.mediaMode === 'imagine'
          ? []
          : await fetchReferences(active.id, input.brief, input.template?.subjectTerms ?? []);
      // Auto discovery must not re-add photos removed from the wizard's explicit selection.
      const referenceIds = refs
        .filter((r) => r.type !== 'media' || !input.pinnedIds || input.pinnedIds.includes(r.id))
        .map((r) => r.id);
      const mediaMode =
        input.mediaMode ?? (refs.some((r) => r.type === 'media') ? mediaModeFor('ai') : 'imagine');
      let p: PostDto;
      if (post) {
        p = await postsApi.update(post.id, {
          brief: input.brief,
          referenceIds,
          mediaMode,
          ...(cards.length ? {} : { format: input.format }),
        });
        setPost(p);
      } else
        p = await ensurePost({
          format: input.format,
          brief: input.brief,
          referenceIds,
          pinnedIds: input.pinnedIds,
          mediaMode,
          template: input.template,
          inspirationPostId: input.inspirationPostId,
        });
      setBusy(false);
      await runGeneration(
        p,
        input.title,
        input.brief,
        undefined,
        after?.onDone,
        after ? (e?: unknown) => after.onFailed(p, e) : undefined,
      );
    } catch (e) {
      showError(e);
      setBusy(false);
    }
  };
  /** A reel is made on its own page, from the idea's words; a picture is made here. */
  const toReel = (line: string) =>
    void navigate('/app/reel', { state: { fresh: crypto.randomUUID(), line } });
  /** An idea is a new picture made from the listing photos, or from its words when there are none. */
  const ideaMode: PostDto['mediaMode'] = photos.length ? 'reimagine' : 'imagine';
  /** Cards an idea draws, as the server counts them: the draft's pinned photos, or four for a carousel. */
  const ideaCards = (format: PostFormat) =>
    cardCountFor(
      format,
      ideaMode === 'imagine'
        ? 0
        : (post?.pinnedIds ?? []).filter((id) => photos.some((m) => m.id === id)).length,
    );
  const ideaCost = (format: PostFormat) =>
    creditSpend({ format, mediaMode: ideaMode, cards: ideaCards(format) });
  /** No photo to start from and no Imagine on this plan: the idea's way on is adding a photo. */
  const ideaNeedsPhoto = ideaMode === 'imagine' && !canImagine;
  const makeIdea = (idea: Idea) =>
    idea.format === 'Reel'
      ? toReel(idea.brief)
      : ideaNeedsPhoto
        ? fileInput.current?.click()
        : void makeFromBrief({
            title: idea.title,
            brief: idea.brief,
            format: idea.format,
            mediaMode: ideaMode,
          });
  /** Make it, from the three steps: with AI, the shown photos pinned, the shape's brief, one generation. */
  /**
   * A reel from the first screen: the post is created, its storyboard starts, and the reel page
   * takes over on the building step. When the wallet is short the credits sheet opens first and
   * the build runs by itself once the pack lands.
   */
  const buildReel = (start: ReelStart) => {
    const cost = reelQuote('storyboard', start.seconds).credits;
    const total = cost + reelQuote('video', start.seconds).credits;
    const run = async () => {
      setBusy(true);
      setError(null);
      try {
        const postId =
          reelPost.current ??
          (
            await createReelPost(active.id, {
              brief: start.line,
              channels: active.channels,
              template: start.template,
              inspirationPostId: start.following?.inspirationPostId ?? null,
            })
          ).id;
        reelPost.current = postId;
        await startReelJob(postId, {
          kind: 'storyboard',
          seconds: start.seconds,
          fidelity: start.fidelity,
          revision: '',
        });
        saveWizard(active.id, null);
        void qc.invalidateQueries({ queryKey: keys.me });
        void qc.invalidateQueries({ queryKey: keys.posts(active.id) });
        void navigate(`/app/reel?post=${postId}`);
      } catch (e) {
        const short = creditShortfall(e);
        if (short) creditsSheet.open(short.needed, () => void run(), 'build the storyboard', total);
        else setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    };
    if (credits && credits.balance < cost)
      creditsSheet.open(cost, () => void run(), 'build the storyboard', total);
    else void run();
  };
  /** `paid`: run after a pack landed, so the wallet this render saw is out of date. */
  const makeShape = (input: WizardMake, paid = false) => {
    setJustAdded(null);
    const brief = wizardBrief(input.shape, input.line, input.input);
    setMoment(
      [
        input.line.trim(),
        `${input.shape.title} on ${input.kind === 'Reel' ? 'the reel' : 'the photos'}.`,
      ]
        .filter(Boolean)
        .join('. ')
        .replace(/\.\./g, '.'),
    );
    setFirstFail(null);
    if (input.path === 'own') {
      saveWizard(active.id, null);
      void makeByHand(input, brief);
      return;
    }
    const mediaMode = input.picks.length ? mediaModeFor(input.path, input.shape.id) : 'imagine';
    // A wallet known to be short opens the credits sheet before anything is made, the way the
    // reel's first step does; the make runs by itself once a pack lands. The steps stay as they are.
    // Priced per card as the server counts them: the picked photos become the post's pins.
    const cost = creditSpend({
      format: input.format,
      mediaMode,
      cards: cardCountFor(
        input.format,
        input.picks.filter((m) => m.kind === 'photo').length,
        cardsPromisedBy(input.shape.brief, mediaMode),
      ),
    });
    if (!paid && credits && credits.balance < cost) {
      creditsSheet.open(cost, () => makeShape(input, true), 'make it', undefined, {
        balance: credits.balance,
      });
      return;
    }
    // Made: the next Create starts empty. A make that does not come out puts this back.
    const kept = loadWizard(active.id);
    saveWizard(active.id, null);
    void makeFromBrief(
      {
        title: input.shape.title,
        brief,
        format: input.format,
        pinnedIds: input.picks.map((m) => m.id),
        mediaMode,
        template: input.template,
        inspirationPostId: input.inspirationPostId,
      },
      {
        onDone: () => {},
        onFailed: (p, e) => {
          const short = creditShortfall(e);
          // A refusal at the door is not a generation that failed: nothing ran and nothing was
          // charged, so the host reads the API's own reason (a plan limit, a property off the plan).
          backToPhotos(
            p,
            kept,
            short
              ? `This needs ${short.needed} credits and you have ${short.balance}.`
              : e instanceof ApiError
                ? e.message
                : undefined,
          );
          // The server's numbers, not the page's: the sheet opens on them, and Make it runs again
          // by itself once a pack lands.
          if (short)
            creditsSheet.open(
              short.needed,
              () => makeShape(input, true),
              'make it',
              undefined,
              short,
            );
        },
      },
    );
  };
  /**
   * A first make that did not come out: the empty draft it opened is archived, and the host is back on
   * the photos with the words, photos and shape as they were. Their credits came back with the run.
   */
  const backToPhotos = (p: PostDto, kept: ReturnType<typeof loadWizard>, why?: string) => {
    if (kept) saveWizard(active.id, kept);
    if (!p.currentVersionId) void postsApi.archive(p.id).catch(() => {});
    setError(null);
    setRetry(null);
    setFirstFail(
      why ??
        'That one didn’t come out. Your credits are back. Make it again, or change the photos or the words.',
    );
    setParams(slotQuery);
    void qc.invalidateQueries({ queryKey: keys.posts(active.id) });
    // The line says the credits are back: the wallet follows, whichever way the run ended.
    void qc.invalidateQueries({ queryKey: keys.me });
  };
  /**
   * By hand: the tapped photos are the post, as they are, flat cards in tap order, no credit,
   * and the caption is left to the host. A video is a reel on its own and is prepared as it is.
   */
  const makeByHand = async (input: WizardMake, brief: string) => {
    setBusy(true);
    setError(null);
    try {
      if (input.kind === 'Reel') {
        const video = input.picks.find((m) => m.kind === 'video');
        if (!video) throw new Error('A reel by hand needs a video. Add one from this device.');
        const p = await ensurePost({ format: 'Reel', brief, pinnedIds: [video.id] });
        setBusy(false);
        const copy = splitCaption(p.caption);
        await runGeneration(
          p,
          'Your reel',
          '',
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          { headline: p.title, caption: copy.body, hashtags: copy.hashtags },
        );
        return;
      }
      const usable = input.picks.filter((m) => m.kind === 'photo' && !!m.url).slice(0, 10);
      if (!usable.length) throw new Error('Pick at least one photo.');
      setPlacing(true);
      const p = await ensurePost({
        format: input.format,
        brief,
        pinnedIds: usable.map((m) => m.id),
      });
      const fresh: ComposerCard[] = [];
      for (const m of usable) fresh.push(await flatCard(p, m, canvas));
      await persist(p, fresh, 'Your photos');
      setCardIdx(0);
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
      setPlacing(false);
    }
  };
  /** Explicit caption controls bypass planning; free text keeps the selected card as context. */
  const words = (title: string, note: string, operation?: GenerationOperation) => {
    if (!post) return;
    void runGeneration(
      post,
      title,
      note,
      undefined,
      undefined,
      undefined,
      operation,
      operation === 'copy_edit' ? [] : [String(cardIdx)],
    );
  };

  /* ------------------------------------------------------------- Imagine */

  const imagine = async (action: string, prompt?: string) => {
    setAskSeason(false);
    setBusy(true);
    setError(null);
    try {
      const p =
        post ??
        (await ensurePost({ format: 'Image', brief: prompt ?? action, mediaMode: 'imagine' }));
      setPost(p);
      setBusy(false);
      await runGeneration(
        p,
        action,
        prompt ?? action,
        undefined,
        undefined,
        undefined,
        action === 'AI artwork' || NEW_PICTURE.includes(action) || !p.currentVersionId
          ? 'new_image'
          : 'image_edit',
        [String(cardIdx)],
      );
    } catch (e) {
      showError(e);
      setBusy(false);
    }
  };
  const keepProposal = async () => {
    if (!post || !proposal) return;
    const parent = post.versions.find((v) => v.id === proposal.parentId);
    const result = post.versions.find((v) => v.id === proposal.resultId);
    const made = result?.cards[0];
    if (!parent || !made) return;
    setBusy(true);
    try {
      const origin =
        proposal.action === 'AI artwork' || NEW_PICTURE.includes(proposal.action)
          ? 'ai'
          : 'ai-edited';
      const inserted = { ...made, mediaId: null, edit: null, origin } as ComposerCard;
      const next = [
        ...parent.cards.slice(0, proposal.index + 1),
        inserted,
        ...parent.cards.slice(proposal.index + 1),
      ];
      const saved = await persist(post, next, `Imagine · ${proposal.action}`);
      setProposal(null);
      closeTool();
      setCardIdx(
        Math.min(
          proposal.index + 1,
          (saved.versions.find((v) => v.id === saved.currentVersionId)?.cards.length ?? 1) - 1,
        ),
      );
      show({
        text: `Added as picture ${proposal.index + 2}, marked AI. Your original is still picture ${proposal.index + 1}.`,
      });
    } catch (e) {
      showError(e);
    } finally {
      setBusy(false);
    }
  };
  const discardProposal = async () => {
    if (!post || !proposal) return;
    try {
      setPost(await postsApi.update(post.id, { currentVersionId: proposal.parentId }));
    } catch (e) {
      showError(e);
    }
    setProposal(null);
    closeTool();
  };

  /* ------------------------------------------------------------- caption, undo, share */

  const saveCaption = async (next: { body?: string; hashtags?: string[] } = {}) => {
    if (!post) return;
    const joined = joinCaption({
      body: next.body ?? body,
      link: parts.link,
      hashtags: next.hashtags ?? parts.hashtags,
    });
    if (joined === post.caption) return setBodyEdit(null);
    try {
      setPost(await postsApi.update(post.id, { caption: joined }));
      setBodyEdit(null);
    } catch (e) {
      showError(e);
    }
  };
  const setLink = async (link: PostLinkInput) => {
    if (!post) return;
    try {
      setPost(await postsApi.update(post.id, { link }));
    } catch (e) {
      showError(e);
    }
  };
  const selectVersion = async (id: string | null, preserveCaption = false) => {
    if (!post || !id) return;
    try {
      setPost(
        await postsApi.update(post.id, {
          currentVersionId: id,
          ...(preserveCaption ? { caption } : {}),
        }),
      );
      setProposal(null);
      closeTool();
    } catch (e) {
      showError(e);
    }
  };
  const confirmSchedule = async (input: {
    date: string;
    time: string;
    publishNow: boolean;
    tiktok?: TikTokOptions;
  }) => {
    if (!post) return;
    setBusy(true);
    setBusyText(null);
    setError(null);
    try {
      const { post: scheduled, generation } = await postsApi.schedule(post.id, {
        ...input,
        channels,
        // The sheet's day and time are a wall clock on the property's zone (the browser's until
        // the property has one); say which.
        timezone: active.timezone ?? hostTimeZone(),
      });
      qc.setQueryData(keys.post(scheduled.id), scheduled);
      let g = generation;
      while (g && g.status === 'running') {
        setBusyText(g.statusText || 'Rewriting the words…');
        await new Promise((r) => window.setTimeout(r, 1500));
        g = await postsApi.poll(g.id);
      }
      if (g?.post) qc.setQueryData(keys.post(g.post.id), g.post);
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      void qc.invalidateQueries({ queryKey: keys.plan(active.id) });
      await qc.invalidateQueries({ queryKey: keys.me });
      saveNext(active.id, null);
      if (input.publishNow)
        void navigate('/app/posts', {
          state: { tab: 'Scheduled', toast: `Sharing now · ${channels.join(', ')}` },
        });
      else
        void navigate('/app/calendar', {
          state: {
            newPostId: scheduled.id,
            toast: `Scheduled · ${dayLabel(new Date(input.date + 'T00:00:00'))} · ${input.time}`,
          },
        });
    } catch (e) {
      showError(e);
      setBusy(false);
      setBusyText(null);
    }
  };
  /** Off the calendar: a draft again, nothing goes out. The channels and the day stay for later. */
  const [unscheduling, setUnscheduling] = useState(false);
  const [unscheduled, setUnscheduled] = useState(false);
  // The button that did it goes away with the status; focus moves to Share, its neighbour.
  const shareRef = useRef<HTMLButtonElement>(null);
  const unschedule = async () => {
    if (!post) return;
    setUnscheduling(true);
    setError(null);
    try {
      const p = await postsApi.unschedule(post.id);
      qc.setQueryData(keys.post(p.id), p);
      await qc.invalidateQueries({ queryKey: keys.posts(active.id) });
      void qc.invalidateQueries({ queryKey: keys.plan(active.id) });
      setUnscheduled(true);
      show({ text: 'Moved back to drafts · nothing will go out' });
      window.setTimeout(() => shareRef.current?.focus(), 0);
    } catch (e) {
      showError(e);
    } finally {
      setUnscheduling(false);
    }
  };
  const startOver = () => {
    stopPolling();
    setError(null);
    setShortfall(null);
    setAsk('');
    setGen(null);
    setMoment(null);
    setProposal(null);
    closeTool();
    setBodyEdit(null);
    setParams(slotQuery);
  };

  /* ------------------------------------------------------------- render */

  const takenDates = new Set(
    (posts.data ?? [])
      .filter((p) => p.scheduledDate && p.status !== 'draft' && p.id !== post?.id)
      .map((p) => p.scheduledDate!),
  );
  const status =
    post?.status === 'published'
      ? 'Shared'
      : post?.status === 'scheduled'
        ? 'Scheduled'
        : post?.status === 'failed'
          ? 'Failed'
          : 'Draft';
  const title = gen
    ? gen.title
    : post
      ? post.title ||
        (empty && !initialCandidate ? 'New post' : formatLabel(post.format, previewCards.length))
      : 'New post';
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOver(false);
    if (e.dataTransfer.files.length) void upload(e.dataTransfer.files);
  };
  const editing = !!draft && !!tool && tool !== 'Imagine' && tool !== 'Reel';
  const images = previewCards.length
    ? previewCards.map((c) => c.imageUrl)
    : [previewVersion?.imageUrl ?? null];
  const stripTools: { key: Tool; label: string }[] = isReel
    ? [{ key: 'Reel', label: 'Edit reel · trim, cover, text, sound' }]
    : [
        { key: 'Crop', label: 'Crop' },
        { key: 'Filters', label: 'Filters' },
        { key: 'Adjust', label: 'Adjust' },
        { key: 'Text', label: 'Text' },
        { key: 'Sticker', label: 'Sticker' },
      ];
  const proposalCards =
    proposal && post
      ? {
          before:
            post.versions.find((v) => v.id === proposal.parentId)?.cards[proposal.index] ?? null,
          after: post.versions.find((v) => v.id === proposal.resultId)?.cards[0] ?? null,
        }
      : null;

  useEffect(() => {
    if (wizard) return;
    return registerDraft?.({
      busy: busy || wording,
      save: async () => {
        if (reelOpen) throw new Error('Save or close the reel editor before switching properties.');
        if (proposal)
          throw new Error('Keep or discard the proposed picture before switching properties.');
        if (draft) await doneEditing(true);
        if (post && bodyEdit !== null) {
          setPost(await postsApi.update(post.id, { caption }));
          setBodyEdit(null);
        }
      },
      discard: async () => {
        if (post && proposal) {
          setPost(await postsApi.update(post.id, { currentVersionId: proposal.parentId }));
          setProposal(null);
        }
        if (wizard) saveWizard(active.id, null);
        setBodyEdit(null);
        setDraft(null);
      },
    });
  });

  const frame = (
    <div
      className={`nx-preview-wrap${vertical ? ' vertical' : ''}${over ? ' drop' : ''}`}
      onDragOver={(e) => {
        e.preventDefault();
        if (!over) setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false);
      }}
      onDrop={onDrop}
    >
      {empty && !initialCandidate && !gen && !placing ? (
        <div className="nx-frame">
          <div className="nx-frame-top">
            <span
              className="nx-avatar"
              style={mediaStyle({ url: ig?.avatarUrl ?? active.heroUrl })}
            />
            <span>{handle.slice(1)}</span>
            <span style={{ marginLeft: 'auto', color: '#999' }}>…</span>
          </div>
          <div className="nx-canvas empty" style={{ aspectRatio: '4 / 5' }}>
            <span style={{ fontSize: 34 }} aria-hidden>
              📷
            </span>
            <span className="nx-empty-title">Start with a photo or video</span>
            <button
              type="button"
              className="nx-chip on"
              onClick={() => fileInput.current?.click()}
              disabled={busy}
            >
              {busy ? 'Working…' : 'Add from this device'}
            </button>
            <button
              type="button"
              className="nx-chip"
              onClick={() => setSheet({ kind: 'library' })}
              disabled={!photos.length}
            >
              Choose from your {photos.length} listing photos
            </button>
            <span style={{ fontSize: 12, color: '#999', marginTop: 6 }}>
              or drop files on this post
            </span>
          </div>
          <div className="nx-frame-actions">
            <span>♡</span>
            <span>○</span>
            <span>▷</span>
            <span style={{ marginLeft: 'auto' }}>⊡</span>
          </div>
          <div className="nx-frame-caption">
            <span className="muted">Your caption will show here</span>
          </div>
        </div>
      ) : (gen || placing) && !cards.length && !version?.videoUrl ? (
        <div className="nx-frame">
          <div className="nx-frame-top">
            <span
              className="nx-avatar"
              style={mediaStyle({ url: ig?.avatarUrl ?? active.heroUrl })}
            />
            <span>{handle.slice(1)}</span>
          </div>
          <div
            className="nx-canvas"
            style={{ aspectRatio: vertical ? '9 / 16' : '4 / 5', background: '#e9e5dc' }}
          >
            <div className="shimmer" />
          </div>
          <div className="nx-frame-caption">
            <b>{handle.slice(1)}</b>
            <span className="nx-cursor">{caption}</span>
          </div>
        </div>
      ) : editing && draft ? (
        <div className="nx-frame">
          <div className="nx-frame-top">
            <span
              className="nx-avatar"
              style={mediaStyle({ url: ig?.avatarUrl ?? active.heroUrl })}
            />
            <span>{handle.slice(1)}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#a5600a', fontWeight: 700 }}>
              editing picture {cardIdx + 1}
            </span>
          </div>
          <EditorStage
            draft={draft}
            tool={tool}
            onDraft={(next) =>
              setDraft((current) =>
                current ? (typeof next === 'function' ? next(current) : next) : null,
              )
            }
            busy={busy}
          />
          <div className="nx-frame-caption">
            <b>{handle.slice(1)}</b>
            {caption}
          </div>
        </div>
      ) : (
        <>
          <PostPreview
            channel={shownPreview}
            width={previewWidth}
            format={post!.format}
            images={images}
            videoUrl={previewVersion?.videoUrl}
            caption={initialCandidate?.caption ?? caption}
            name={previewConnection?.displayName || active.name}
            handle={
              previewConnection?.handle ? `@${previewConnection.handle.replace(/^@/, '')}` : handle
            }
            location={post?.location?.name ?? active.location.split(',')[0] ?? ''}
            avatarUrl={previewConnection?.avatarUrl ?? active.heroUrl}
            cardIdx={Math.min(cardIdxRaw, Math.max(0, previewCards.length - 1))}
            onCardIdx={setCardIdx}
            canvas={canvas}
            pictureContent={
              !initialCandidate && !isReel && card?.edit && post ? (
                <InlinePicture
                  key={`${version?.id}-${cardIdx}`}
                  edit={card.edit}
                  busy={working}
                  onSave={async (edit) => {
                    setBusy(true);
                    try {
                      const rendered = await renderCrop(edit);
                      const asset = await storeCardAsset(post.id, rendered.blob);
                      await persist(
                        post,
                        cards.map((c, i) =>
                          i === cardIdx
                            ? { ...c, imageUrl: asset.url, mediaId: asset.id, edit: rendered.edit }
                            : c,
                        ),
                        'Text updated',
                      );
                    } catch (e) {
                      showError(e);
                      throw e;
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              ) : undefined
            }
            onEdit={
              initialCandidate
                ? undefined
                : () =>
                    isReel
                      ? openTool('Reel')
                      : card &&
                        openTool(card.edit?.layers.some((l) => l.kind === 'text') ? 'Text' : 'Crop')
            }
          />
          {gen && <div className="shimmer slow" style={{ pointerEvents: 'none' }} />}
        </>
      )}
      {over && <div className="nx-drop-hint">Drop to add</div>}
    </div>
  );

  if (reelFlow) return <Navigate to={`/app/reel${postId ? `?post=${postId}` : ''}`} replace />;

  return (
    <div className="nx a-fade">
      <input
        ref={fileInput}
        type="file"
        hidden
        multiple
        accept="image/*,video/mp4"
        aria-label="Add photos or video"
        onChange={(e) => {
          if (e.target.files?.length) void upload(e.target.files);
          e.target.value = '';
        }}
      />
      {wizard ? (
        <FlowHeader
          place={active.name}
          title={title}
          railRef={setRailSlot}
          newLabel="New post"
          saved={false}
          note={targetDate ? `For ${dayWithMonth(targetDate)}` : null}
        />
      ) : (
        <div className="nx-header">
          {(hasPost || gen) && (
            <button
              type="button"
              className="icon-btn hv"
              aria-label="New post"
              title="New post"
              onClick={startOver}
            >
              ←
            </button>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="nx-title">
              {active.name} · {title}
            </div>
            {!wizard && (
              <div className="nx-sub">
                {gen
                  ? 'Making…'
                  : post
                    ? formatLabel(post.format, Math.max(1, previewCards.length))
                    : ''}
              </div>
            )}
          </div>
          {targetDate && !post?.scheduledDate && (
            <span
              className="nx-status draft"
              title="Picked on the calendar; Share opens on this day"
            >
              {`For ${dayWithMonth(targetDate)}`}
            </span>
          )}
          {hasPost && !empty && (
            <span className={`nx-status ${status.toLowerCase()}`}>
              {status}
              {post?.scheduledDate && status === 'Scheduled'
                ? ` · ${dayLabel(new Date(post.scheduledDate + 'T00:00:00'))} ${post.scheduledTime ?? ''}`
                : ''}
              {status === 'Draft' ? ' · saved' : ''}
            </span>
          )}
          <Link
            to="/app/posts"
            state={{ tab: 'Drafts' }}
            className="nx-chip nx-header-link"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Drafts
          </Link>
          <Link
            to="/app/calendar"
            className="nx-chip nx-header-link"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Calendar
          </Link>
          {!wizard && (post?.status === 'scheduled' || post?.status === 'failed') && (
            <button
              type="button"
              className="btn btn-outline hv"
              disabled={working || unscheduling}
              aria-describedby="nx-unschedule-note"
              onClick={() => void unschedule()}
            >
              {unscheduling ? 'Moving…' : 'Move back to drafts'}
            </button>
          )}
          <span id="nx-unschedule-note" role="status" aria-live="polite" className="sr-only">
            {unscheduled && post?.status === 'draft' ? 'Moved back to drafts' : ''}
          </span>
          {/* Only our own R2 media can be handed over; an older reel points at a remote bucket. */}
          {!wizard && previewVersion?.videoUrl?.startsWith('/api/media/') && (
            <a
              className="btn hv"
              href={`${previewVersion.videoUrl}?download=1`}
              // The file downloads because the response says attachment, not because of this hint.
              download
            >
              Download
            </a>
          )}
          {!wizard && (
            <button
              type="button"
              ref={shareRef}
              className="btn btn-primary hv"
              disabled={empty || working || post?.status === 'published'}
              onClick={() => setSheet({ kind: 'share' })}
            >
              {post?.status === 'published'
                ? 'Shared ✓'
                : post?.status === 'scheduled'
                  ? 'Reschedule ▸'
                  : 'Share ▸'}
            </button>
          )}
        </div>
      )}

      {wizard && (
        <>
          {offPlan && (
            <div role="status" aria-label="Plan" className="nx-plan-notice">
              <span>
                {offPlan} · from {money(cheapestMonthly)} a month.
              </span>
              <Link to="/app/settings">Settings</Link>
            </div>
          )}
          <Wizard
            key={wizardKey}
            propertyId={active.id}
            photos={photos.filter((m) => !!m.url)}
            videos={videos}
            reviews={property.data?.reviews ?? []}
            bookingUrl={active.bookingUrl ?? null}
            busy={busy}
            adding={uploading}
            credits={credits}
            justAdded={justAdded}
            blocked={offPlan}
            onFind={(_kind, line, terms) =>
              fetchReferences(active.id, line, terms).then((refs) =>
                refs.filter((r) => r.type === 'media').map((r) => ({ id: r.id, why: r.why })),
              )
            }
            onUpload={() => fileInput.current?.click()}
            onDrop={(files) => void upload(files)}
            startOwn={params.get('own') === '1'}
            onReel={buildReel}
            railSlot={railSlot}
            failure={firstFail}
            canImagine={canImagine}
            onMake={(input) => makeShape(input)}
          />
          {creditsSheet.element}
          {error && (
            <span role="alert" style={{ color: '#b3261e', fontSize: 12.5, padding: '0 48px 16px' }}>
              {error}{' '}
              {retry && retry.error === error && (
                <button type="button" className="nx-retry" onClick={tryAgain}>
                  Try again
                </button>
              )}
            </span>
          )}
          {shortPanel && <div style={{ padding: '0 48px 16px' }}>{shortPanel}</div>}
        </>
      )}

      {!wizard && (
        <div className="nx-body">
          <div className="nx-stage dotgrid" ref={stageRef}>
            {!empty && !gen && !editing && (
              <div className="seg round" role="radiogroup" aria-label="Preview as">
                {previewChannels.map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={shownPreview === k}
                    className={`seg-item hv${shownPreview === k ? ' on' : ''}`}
                    onClick={() => setPreview(k)}
                  >
                    {CHANNEL_META[k].label}
                  </button>
                ))}
                <span
                  style={{ fontSize: 11, color: '#999', padding: '0 8px', alignSelf: 'center' }}
                >
                  {previewChannels.length
                    ? 'Preview'
                    : 'Preview · connect a channel to see its layout'}
                </span>
              </div>
            )}
            {frame}

            {!empty && (
              <div className="nx-strip" aria-label="Pictures">
                {cards.map((c, i) => (
                  <div
                    key={`${c.mediaId ?? c.imageUrl ?? i}`}
                    className={`nx-thumb${cardIdx === i ? ' on' : ''}${drag?.from === i ? ' dragging' : ''}${drag?.over === i ? ' over' : ''}`}
                    draggable={!working && !editing && cards.length > 1}
                    onDragStart={() => setDrag({ from: i, over: null })}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (drag && drag.over !== i) setDrag({ ...drag, over: i });
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!drag || drag.from === i) return setDrag(null);
                      const next = [...cards];
                      const [moved] = next.splice(drag.from, 1);
                      next.splice(i, 0, moved!);
                      setDrag(null);
                      setCardIdx(i);
                      void arrange(next, 'Reordered');
                    }}
                    onDragEnd={() => setDrag(null)}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      aria-label={`Picture ${i + 1}`}
                      className="nx-thumb-pic"
                      style={{ ...mediaStyle({ url: c.imageUrl, scene: version?.scene }) }}
                      onClick={() => {
                        if (editing) return;
                        setCardIdx(i);
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && setCardIdx(i)}
                    >
                      {i === 0 && cards.length > 1 && <span className="nx-cover-tag">cover</span>}
                    </div>
                    <span
                      className={`nx-thumb-label${c.origin && c.origin !== 'host' ? ' ai' : ''}`}
                    >
                      {c.origin === 'ai' ? 'AI' : c.origin === 'ai-edited' ? 'AI-edited' : 'Yours'}
                    </span>
                  </div>
                ))}
                {gen &&
                  !cards.length &&
                  [0, 1, 2].map((i) => (
                    <div key={i} className="nx-skel">
                      <div className="shimmer" />
                    </div>
                  ))}
                {!gen && !isReel && cards.length > 0 && cards.length < 10 && (
                  <button
                    type="button"
                    className="nx-thumb-add"
                    aria-label="Add a picture"
                    title="Add a picture"
                    disabled={working || editing}
                    onClick={() => setSheet({ kind: 'library' })}
                  >
                    +
                  </button>
                )}
              </div>
            )}

            {isReel && !gen && version && (
              <FilmedFrom ids={version.referenceMediaIds ?? []} library={photos} />
            )}

            {!empty && !gen && (card || isReel) && (
              <div className="nx-tools" aria-label="Picture tools">
                {stripTools.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className={`nx-tool${tool === t.key ? ' on' : ''}`}
                    aria-pressed={tool === t.key}
                    disabled={busy}
                    onClick={() => openTool(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
                {!isReel && (
                  <button
                    type="button"
                    className={`nx-tool ai${tool === 'Imagine' ? ' on' : ''}`}
                    aria-pressed={tool === 'Imagine'}
                    disabled={busy}
                    onClick={() => openTool('Imagine')}
                  >
                    ✦ Imagine
                  </button>
                )}
                {!isReel && (
                  <>
                    <span
                      style={{
                        width: 1,
                        height: 18,
                        background: 'rgba(0,0,0,.1)',
                        margin: '0 4px',
                      }}
                    />
                    <button
                      type="button"
                      className="nx-tool quiet"
                      disabled={busy || editing}
                      onClick={() => setSheet({ kind: 'library', replace: cardIdx })}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="nx-tool quiet"
                      disabled={busy || editing || cards.length < 2}
                      title={
                        cards.length < 2
                          ? 'The last picture stays. Start a new post instead.'
                          : 'Remove this picture'
                      }
                      onClick={() =>
                        void arrange(
                          cards.filter((_, i) => i !== cardIdx),
                          'Picture removed',
                        )
                      }
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
            )}
            {cards.length > 1 && !gen && !editing && (
              <span style={{ fontSize: 11, color: '#999' }}>drag a thumbnail to reorder</span>
            )}
            {error && (
              <span
                role="alert"
                style={{ color: '#b3261e', fontSize: 12.5, maxWidth: 420, textAlign: 'center' }}
              >
                {error}{' '}
                {retry && retry.error === error && (
                  <button type="button" className="nx-retry" onClick={tryAgain}>
                    Try again
                  </button>
                )}
              </span>
            )}
            {shortPanel}
          </div>

          <aside className="nx-side">
            {gen ? (
              <MakingPanel
                title={gen.title}
                renderOf={gen.renderOf}
                statuses={gen.statuses}
                moment={moment}
                note={gen.note}
                firstMake={!post?.versions.length}
                format={post?.format ?? null}
                mediaMode={post?.mediaMode ?? null}
                captionOnly={gen.captionOnly}
                onStop={() => void stopGeneration()}
              />
            ) : wording ? (
              <>
                <h3>Putting the words on</h3>
                <div className="nx-steps">
                  <div className="nx-step doing">
                    <span className="m">●</span>
                    <span>Your headline on each picture…</span>
                  </div>
                </div>
                <p className="nx-note">
                  A few seconds. Move it, restyle it or delete it under Text after.
                </p>
              </>
            ) : placing ? (
              <>
                <h3>Putting your photos in</h3>
                <div className="nx-steps">
                  <div className="nx-step doing">
                    <span className="m">●</span>
                    <span>As they are, in your order…</span>
                  </div>
                </div>
                <p className="nx-note">
                  A few seconds. Nothing is generated; the caption is yours.
                </p>
              </>
            ) : initialCandidate ? (
              <>
                <h3>Your first draft is ready</h3>
                <p className="nx-note">
                  Review the preview, then Keep this version to edit or share it, or Discard to
                  start again.
                </p>
              </>
            ) : empty ? (
              <>
                <h3>Or start from an idea</h3>
                <p className="nx-note" style={{ marginTop: -4 }}>
                  Made from your {photos.length} listing photos and your reviews. Nothing invented.
                </p>
                {ideas.map((idea, i) => {
                  return (
                    <div
                      key={idea.id}
                      className="nx-idea"
                      style={{ animationDelay: `${i * 0.06}s` }}
                      role="button"
                      tabIndex={0}
                      aria-label={`Make it: ${idea.title}${idea.format === 'Reel' ? '' : ideaNeedsPhoto ? ' · add a photo first' : ` · ${ideaCost(idea.format)} credits`}`}
                      onClick={() => !working && makeIdea(idea)}
                      onKeyDown={(e) => e.key === 'Enter' && makeIdea(idea)}
                    >
                      <span className="p" style={mediaStyle({ scene: idea.scene })} />
                      <span
                        style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}
                      >
                        <span className="t">{idea.title}</span>
                        <span className="w">
                          {ideaNeedsPhoto && idea.format !== 'Reel'
                            ? 'Add a photo to make this'
                            : idea.format === 'Carousel'
                              ? `${ideaCards(idea.format)} ${ideaMode === 'imagine' ? 'imagined' : 'reimagined'} pictures`
                              : idea.format === 'Reel'
                                ? 'Reel'
                                : `1 ${ideaMode === 'imagine' ? 'imagined' : 'reimagined'} picture`}
                          {idea.format === 'Reel' || ideaNeedsPhoto
                            ? ''
                            : ` · ${ideaCost(idea.format)} credits`}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="x"
                        aria-label={`Not this week: ${idea.title}`}
                        title="Not this week"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSkipped((sk) => [...sk, idea.id]);
                          void postsApi
                            .skipIdea(idea.id)
                            .then(() =>
                              qc.invalidateQueries({ queryKey: keys.property(active.id) }),
                            );
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <p className="nx-note" style={{ marginTop: 8 }}>
                  {!canImagine
                    ? 'A made-up picture for an offer or the season (AI artwork) unlocks with your plan.'
                    : 'Want a made-up picture for an offer or the season? '}
                  <button
                    type="button"
                    hidden={!canImagine}
                    disabled={working || !canImagine}
                    onClick={() => setSheet({ kind: 'artwork' })}
                    style={{
                      border: 0,
                      background: 'none',
                      padding: 0,
                      color: '#a5600a',
                      fontWeight: 700,
                      cursor: 'pointer',
                      fontSize: 'inherit',
                    }}
                  >
                    AI artwork
                  </button>
                  {canImagine ? ', always marked AI.' : ''}
                </p>
              </>
            ) : tool === 'Imagine' && card ? (
              <>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <h3 style={{ margin: 0 }}>✦ Imagine · picture {cardIdx + 1}</h3>
                  <button
                    type="button"
                    className="nx-chip"
                    onClick={() => void (proposal ? discardProposal() : closeTool())}
                  >
                    {proposal ? 'Discard' : 'Done ✓'}
                  </button>
                </div>
                <div
                  className="nx-imagine"
                  style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}
                >
                  <p className="nx-note">
                    Every result is a new card marked AI. Your original stays. This changes how your
                    place looks, and guests may notice.
                  </p>
                  {proposalCards ? (
                    <>
                      <div className="nx-ba">
                        <div style={mediaStyle({ url: proposalCards.before?.imageUrl })}>
                          <span>before</span>
                        </div>
                        <div
                          className="nx-settle"
                          style={mediaStyle({ url: proposalCards.after?.imageUrl })}
                        >
                          <span>after · AI</span>
                        </div>
                      </div>
                      <div className="nx-chips">
                        <button
                          type="button"
                          className="nx-chip on"
                          disabled={busy}
                          onClick={() => void keepProposal()}
                        >
                          Keep as new card
                        </button>
                        <button
                          type="button"
                          className="nx-chip"
                          disabled={busy}
                          onClick={() => void discardProposal()}
                        >
                          Discard
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="nx-note" style={{ fontWeight: 700, color: '#555' }}>
                        Change this photo
                      </span>
                      <div className="nx-chips">
                        {CHANGE_PHOTO.map((a) => (
                          <button
                            key={a}
                            type="button"
                            className="nx-chip ai"
                            disabled={working}
                            aria-expanded={a === 'Change season' ? askSeason : undefined}
                            onClick={() =>
                              a === 'Change season' ? setAskSeason((v) => !v) : void imagine(a)
                            }
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                      {askSeason && (
                        <div className="nx-chips" role="group" aria-label="Which season?">
                          {SEASONS.map((season) => (
                            <button
                              key={season}
                              type="button"
                              className="nx-chip ai"
                              disabled={working}
                              onClick={() => void imagine('Change season', seasonPrompt(season))}
                            >
                              {season}
                            </button>
                          ))}
                        </div>
                      )}
                      <span className="nx-note" style={{ fontWeight: 700, color: '#555' }}>
                        New picture of my place{' '}
                        <span style={{ fontWeight: 500 }}>· from your photos</span>
                      </span>
                      <div className="nx-chips">
                        {NEW_PICTURE.map((a) => (
                          <button
                            key={a}
                            type="button"
                            className="nx-chip ai"
                            disabled={working}
                            onClick={() => void imagine(a)}
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                      <span className="nx-note" style={{ fontWeight: 700, color: '#555' }}>
                        AI artwork{' '}
                        <span style={{ fontWeight: 500 }}>
                          {canImagine ? '· no photos used' : '· unlocks with your plan'}
                        </span>
                      </span>
                      <div className="nx-chips">
                        <button
                          type="button"
                          className="nx-chip ai"
                          disabled={working || !canImagine}
                          title={canImagine ? undefined : 'Imagine unlocks with your plan'}
                          onClick={() => setSheet({ kind: 'artwork' })}
                        >
                          Describe it
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </>
            ) : editing && draft && tool ? (
              <>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <h3 style={{ margin: 0 }}>
                    {tool} · picture {cardIdx + 1} of {cards.length}
                  </h3>
                  <span style={{ display: 'flex', gap: 6 }}>
                    <button type="button" className="nx-chip" disabled={busy} onClick={closeTool}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="nx-chip on"
                      disabled={busy || (tool === 'Crop' && !draft.area)}
                      onClick={() => void doneEditing()}
                    >
                      {busy ? 'Saving…' : 'Done ✓'}
                    </button>
                  </span>
                </div>
                <EditorControls
                  draft={draft}
                  tool={tool}
                  onDraft={(next) =>
                    setDraft((current) =>
                      current ? (typeof next === 'function' ? next(current) : next) : null,
                    )
                  }
                  busy={busy}
                  headline={card?.headline ?? ''}
                  count={cards.length}
                  lockedShape={!!vertical}
                  thumbnail={mediaStyle({ url: card?.imageUrl, scene: version?.scene })}
                />
              </>
            ) : (
              <>
                <div className="nx-caption on-side">
                  <div className="nx-caption-head">
                    <span>Caption</span>
                    <button
                      type="button"
                      className="nx-chip ai"
                      style={{ padding: '4px 9px' }}
                      disabled={working}
                      onClick={() => words('Writing the caption', 'New caption', 'copy_edit')}
                    >
                      ✦ Write it for me · 1 credit
                    </button>
                  </div>
                  <textarea
                    value={body}
                    placeholder="Write it yourself, or press ✦"
                    aria-label="Caption"
                    disabled={working}
                    onChange={(e) => setBodyEdit(e.target.value)}
                    onBlur={() => void saveCaption()}
                  />
                  {post?.caption && (
                    <div className="nx-chips">
                      {['Shorter', 'Another take', 'Warmer', 'Add the offer'].map((c) => (
                        <button
                          key={c}
                          type="button"
                          className="nx-chip ai"
                          style={{ padding: '4px 9px', fontSize: 12 }}
                          disabled={working}
                          onClick={() => words(c, `${c}: revise the caption`, 'copy_edit')}
                        >
                          {c} · {REWRITE} credit
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="nx-tags">
                    {parts.hashtags.map((h) => (
                      <span key={h} className="nx-tag">
                        #{h}
                        <button
                          type="button"
                          aria-label={`Remove #${h}`}
                          onClick={() =>
                            void saveCaption({ hashtags: parts.hashtags.filter((x) => x !== h) })
                          }
                        >
                          ×
                        </button>
                      </span>
                    ))}
                    <input
                      value={tagDraft}
                      placeholder="+ tag"
                      aria-label="Add a hashtag"
                      disabled={working}
                      onChange={(e) => setTagDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const t = parseHashtag(tagDraft);
                          setTagDraft('');
                          if (t && !parts.hashtags.includes(t))
                            void saveCaption({ hashtags: [...parts.hashtags, t] });
                        }
                      }}
                    />
                  </div>
                  <div className="nx-toggle">
                    <button
                      type="button"
                      className={`nx-switch${post?.linkMode !== 'off' ? ' on' : ''}`}
                      aria-label="Add my booking link"
                      disabled={working}
                      onClick={() =>
                        void setLink(post?.linkMode === 'off' ? { mode: 'book' } : { mode: 'off' })
                      }
                    />
                    <span>
                      Add my booking link{' '}
                      {post?.trackedLink && post.linkMode !== 'off' && (
                        <span style={{ color: '#888' }}>{post.trackedLink.shortUrl}</span>
                      )}
                      <small>
                        clickable on Facebook · plain text on Instagram · counts clicks per post
                      </small>
                    </span>
                  </div>
                </div>
                {/* Where the post went or is going. A draft is going nowhere: jobs left from a
                    schedule it was moved back from are cancelled, not "Not connected". Which
                    accounts are connected is the Share sheet's to say. */}
                {post && post.status !== 'draft' && post.publishJobs.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <h3>
                      {post.status === 'published'
                        ? 'Shared'
                        : post.status === 'failed'
                          ? 'Sharing failed'
                          : 'Sharing'}
                    </h3>
                    <PublishStatus jobs={post.publishJobs} />
                  </div>
                )}
                {madeAfter && (
                  <p className="nx-note nx-made-after">
                    Made after{' '}
                    <a href={madeAfter.url} target="_blank" rel="noreferrer">
                      @{madeAfter.handle}’s post
                    </a>
                  </p>
                )}
                {post && version && (
                  <PostDetails
                    post={post}
                    version={version}
                    connectionId={
                      (connected.data?.connections ?? []).find(
                        (c) => c.channel === 'IG' && c.status === 'connected',
                      )?.id
                    }
                    onChange={setPost}
                    disabled={working}
                  />
                )}
                <hr />
                {isReel && post && version?.videoUrl && (
                  <>
                    <ReelSoundStep
                      postId={post.id}
                      propertyId={active.id}
                      version={version}
                      disabled={working}
                      onAccepted={(job) => followGeneration(job, 'Adding your music', post.id)}
                      previewVideo={() => stageRef.current?.querySelector('video') ?? null}
                    />
                    <hr />
                  </>
                )}
                <h3>✦ Ask for a change</h3>
                {version?.origin === 'host' && (
                  <p className="nx-note" style={{ marginTop: -6 }}>
                    Your edits stay in the versions rail. A change makes a new version on top.
                  </p>
                )}
                <div className="nx-ask">
                  <input
                    value={ask}
                    placeholder="e.g. warmer light, or name the hot tub in the caption"
                    aria-label="Ask for a change"
                    disabled={working}
                    onChange={(e) => setAsk(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && ask.trim()) {
                        words(ask, ask);
                        setAsk('');
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="nx-chip ai"
                    disabled={!ask.trim() || working}
                    onClick={() => {
                      words(ask, ask);
                      setAsk('');
                    }}
                  >
                    Go · up to {CHANGE_MAX} credits a card
                  </button>
                </div>
                <div className="nx-chips">
                  {!isReel && (
                    <button
                      type="button"
                      className="nx-chip ai"
                      disabled={working || !cards.length}
                      onClick={() => void fixAll()}
                    >
                      {cards.length === 1
                        ? '✦ Auto-fix the light · free'
                        : `✦ Auto-fix the light on all ${cards.length} · free`}
                    </button>
                  )}
                </div>
                <p className="nx-note">
                  {post?.mediaMode === 'imagine'
                    ? 'AI imagery made from your idea. Ask for a change to refine it.'
                    : post?.mediaMode === 'reimagine' || post?.mediaMode === 'enhance'
                      ? 'AI imagery based on your photos. Review details before sharing.'
                      : 'Your original photos. Manual edits keep the source available.'}{' '}
                  Changes make a new version for you to review. Your current version stays saved.
                </p>
              </>
            )}
            {!empty && !gen && (
              <div className="nx-undo">
                <button
                  type="button"
                  disabled={!undoId || working}
                  onClick={() => void selectVersion(undoId, version?.origin === 'host')}
                  aria-label="Undo"
                >
                  ↶ {lastChange && undoId ? `Undo: ${lastChange}` : 'Undo'}
                </button>
                <button
                  type="button"
                  disabled={!redoId || working}
                  onClick={() =>
                    void selectVersion(
                      redoId,
                      versions.find((v) => v.id === redoId)?.origin === 'host',
                    )
                  }
                  aria-label="Redo"
                >
                  ↷
                </button>
              </div>
            )}
            {post &&
              !gen &&
              post.versions
                .filter((v) => v.acceptance === 'unjudged' && v.generationPlanId)
                .sort((a, b) => a.n - b.n)
                .slice(-1)
                .map((candidate) => (
                  <GenerationCandidate
                    key={candidate.id}
                    imageUrl={candidate.coverUrl ?? candidate.imageUrl}
                    imageUrls={candidate.cards.flatMap((card) =>
                      card.imageUrl ? [card.imageUrl] : [],
                    )}
                    videoUrl={candidate.videoUrl}
                    caption={candidate.caption}
                    note={hostWords(candidate.note)}
                    // A first draft fills the stage; a revision is only seen here.
                    media={!!post.currentVersionId}
                    stale={(candidate.baseVersionId ?? null) !== post.currentVersionId}
                    onDecide={async (acceptance) => {
                      setPost(await postsApi.decideGeneration(post.id, candidate.id, acceptance));
                    }}
                  />
                ))}
            {post && debug && (
              <GenerationDetails
                postId={post.id}
                revision={`${post.currentVersionId}:${post.versions.length}:${gen?.statuses.join('|') ?? ''}`}
                owner={active.role === 'owner'}
              />
            )}
            {!empty && !gen && versions.length > 1 && (
              <div className="nx-versions" role="list" aria-label="Versions">
                <span className="label-caps">Versions</span>
                {[...versions]
                  .sort((a, b) => b.n - a.n)
                  .map((v) => {
                    const sel = v.id === post?.currentVersionId;
                    const thumb = v.coverUrl ?? v.imageUrl;
                    return (
                      <span key={v.id} role="listitem">
                        <button
                          type="button"
                          aria-pressed={sel}
                          className={`nx-version${sel ? ' on' : ''}`}
                          disabled={
                            working || (!!v.generationPlanId && v.acceptance !== 'accepted')
                          }
                          onClick={() => void selectVersion(v.id)}
                        >
                          <span
                            className="thumb"
                            style={thumb ? { backgroundImage: `url(${thumb})` } : undefined}
                          />
                          <span className="v">v{v.n}</span>
                          <span className="what">
                            {hostWords(v.note)} · {v.origin === 'ai' ? 'AI' : 'You'}
                          </span>
                        </button>
                      </span>
                    );
                  })}
                <span className="nx-note">Every change keeps the one before it here.</span>
              </div>
            )}
          </aside>
        </div>
      )}

      {toast && (
        <div className="nx-toast" role="status">
          <span>{toast.text}</span>
          {toast.action && (
            <button
              type="button"
              onClick={() => {
                toast.action?.run();
                setToast(null);
              }}
            >
              {toast.action.label}
            </button>
          )}
        </div>
      )}

      {sheet?.kind === 'share' && post && (
        <ScheduleSheet
          post={post}
          version={version}
          format={post.format}
          plan={active.plan}
          channels={channels}
          toggleChannel={(c) =>
            setChannels((cs) => (cs.includes(c) ? cs.filter((x) => x !== c) : [...cs, c]))
          }
          takenDates={takenDates}
          bestTime={active.bestTime}
          timezone={active.timezone ?? undefined}
          initialDate={post.scheduledDate ?? targetDate ?? toIsoDate(new Date())}
          initialTime={targetTime ?? next?.time}
          busy={busy}
          busyText={busyText}
          error={error}
          onClose={() => setSheet(null)}
          onCaption={(next) => void saveCaption({ body: next })}
          onHashtags={(hashtags) => void saveCaption({ hashtags })}
          onConfirm={(input) => void confirmSchedule(input)}
        />
      )}
      {sheet?.kind === 'library' && (
        <LibrarySheet
          photos={photos}
          single={sheet.replace !== undefined}
          onClose={() => setSheet(null)}
          onAdd={(items) => {
            const replace = sheet.replace;
            setSheet(null);
            if (replace !== undefined && items[0]) void replacePhoto(replace, items[0]);
            else void addMedia(items);
          }}
          onUpload={() => {
            setSheet(null);
            fileInput.current?.click();
          }}
        />
      )}
      {sheet?.kind === 'artwork' && (
        <ArtworkSheet
          busy={working}
          onClose={() => setSheet(null)}
          onMake={(prompt) => {
            setSheet(null);
            void imagine('AI artwork', prompt);
          }}
        />
      )}
      {reelOpen && post && version && (
        <ReelSheet
          postId={post.id}
          propertyId={active.id}
          initialTool={reelOpen}
          version={version}
          initial={preserved?.versionId === version.id ? preserved.draft : reelDraft(version)}
          originalSource={reelSource(version, library.data ?? [])}
          onAccepted={(job) => {
            setReelOpen(false);
            followGeneration(job, 'Saving your reel edit', post.id);
          }}
          onPreserve={(d) => setPreserved({ versionId: version.id, draft: d })}
          onCancel={() => setReelOpen(false)}
        />
      )}
    </div>
  );
}
